import { CheckoutContext } from '@/context/CheckoutContext';
import { useContext, useState, useEffect, useMemo } from 'react';
import { Select } from '../Common/Select';
import type { SavedCard } from '@/types/checkout';
import CreditCard from '@/assets/icons/payments/credit-card.svg';
import Add from '@/assets/icons/common/add.svg';
import { DrawerScreen } from '@/types/screens';
import { Button } from '../Common/Button';
import { ButtonType } from '@/types/components';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { isMobileDevice } from '@/utils/device';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import { Popover, PopoverItem } from '../Common/Popover';
import { useRemoveCard } from '@/api/card';
import { MAXIMUM_CREDIT_CARD_ALLOWED } from '@/constants/payment';
import InfoIcon from '@/assets/icons/common/information.png';
import { getCardBrandByProvider, getCardBrandIcon } from '@/utils/cardBrandIcon';
import { getLastFourDigits } from '@/utils/paymentParams';
import Checked from '@/assets/icons/common/checked.svg';
import { useTranslate } from '@/hooks/useTranslate';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { mixpanelService } from '@/services/mixpanelService';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { cn } from '@/utils/cn';

export const SavedCardList = () => {
  const context = useContext(CheckoutContext);
  const {
    paymentMethodsSettings,
    card: { savedCards, selectedCard, selectCard },
    drawer: { closeDrawer, setDrawerScreen },
    viewer: { setViewer },
    actions: { removeCard },
  } = context;
  const [initialSelectedCard, setInitialSelectedCard] = useState<SavedCard>(selectedCard as SavedCard);
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const { t } = useTranslate();

  useEffect(() => {
    if (selectedCard) {
      setInitialSelectedCard(selectedCard);
    }
  }, [selectedCard]);
  const { mutate: removeCardMutation } = useRemoveCard();
  const providerByCard = useMemo(() => {
    return (
      (paymentMethodsSettings.find((pm) => pm.paymentMethodName === PaymentMethodEnum.CREDIT_CARD)
        ?.provider as ProviderEnum) || ProviderEnum.ADYEN
    );
  }, [paymentMethodsSettings]);

  const handleSelect = () => {
    const properties = getMixpanelProperties(context);
    mixpanelService.track(MixpanelEvents.SAVED_CARD_SELECTED, properties as Record<string, unknown>);
    closeDrawer();
    selectCard(initialSelectedCard);
  };

  const handleCancel = () => {
    closeDrawer();
  };

  const isCardSelected = (card: SavedCard) => {
    return card.isNew && initialSelectedCard?.isNew
      ? initialSelectedCard?.id === card.id
      : initialSelectedCard?.pspCardIdentifier === card.pspCardIdentifier;
  };

  const handleDelete = async (_card: SavedCard, event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenPopoverId(null);

    if (!_card.isNew) {
      await removeCardMutation({
        pspCardIdentifier: _card.pspCardIdentifier as string,
        paymentProvider: _card.paymentProvider as ProviderEnum,
      });
    }
    const properties = getMixpanelProperties(context);
    mixpanelService.track(MixpanelEvents.SAVED_CARD_REMOVED, properties as Record<string, unknown>);

    removeCard(_card);

    if (savedCards.length === 1) {
      closeDrawer();
    }
  };
  const handleAddNewCard = () => {
    if (isMobileDevice()) {
      setDrawerScreen(DrawerScreen.NEW_CARD);
    } else {
      setViewer(DrawerScreen.NEW_CARD);
    }
  };

  const handleSelectCard = (card: SavedCard) => {
    setInitialSelectedCard(card);
  };

  const cardsCount = savedCards.length;

  return (
    <div data-testid="saved-card-list" className="flex flex-col gap-4">
      <div data-testid="saved-cards-title" className="text-sm color-card-foreground font-semibold">
        {t('savedCards')}
      </div>
      <div className="flex flex-col gap-2">
        {cardsCount < MAXIMUM_CREDIT_CARD_ALLOWED ? (
          <div
            data-testid="add-new-card-button"
            className="flex flex-row items-center h-13 justify-between w-full gap-2 border-1 border-base-border rounded-xl p-3"
            onClick={handleAddNewCard}
          >
            <div className="flex flex-row items-center gap-2">
              <img src={CreditCard} />
              <div className="text-sm color-base-foreground font-medium">{t('newCard')}</div>
            </div>
            <img src={Add} className="w-5 h-5" />
          </div>
        ) : null}
        {cardsCount >= MAXIMUM_CREDIT_CARD_ALLOWED ? (
          <div className="flex flex-row items-center gap-4 border-1 border-base-border rounded-xl p-3">
            <img src={InfoIcon} className="w-4 h-4" />
            <div className="text-sm color-base-foreground">
              {t('maxCardsReached', { max: MAXIMUM_CREDIT_CARD_ALLOWED })}
            </div>
          </div>
        ) : null}
        {savedCards.map((card, index) => {
          const cardId = `${card.pspCardIdentifier}-${index}`;
          return (
            <Select
              data-testid={`saved-card-${index}`}
              key={cardId}
              id={cardId}
              isSelected={false}
              onSelect={() => handleSelectCard(card)}
              className={cn(isCardSelected(card) && 'border-selected-border')}
            >
              <div className="flex flex-row items-center justify-between w-full">
                <div className="flex flex-row items-center gap-3">
                  <img
                    className="w-8 h-6"
                    data-testid={`card-brand-icon-${index}`}
                    src={getCardBrandIcon(getCardBrandByProvider(providerByCard, card) as string)}
                    alt={card.brand}
                  />
                  <div
                    data-testid={`card-number-${index}`}
                    className="text-sm color-base-foreground flex flex-row items-center gap-1"
                  >
                    ****{' '}
                    {getLastFourDigits(
                      (card.cardNumber as string) ??
                        card.cardLastFourDigits ??
                        (card.cardDetails?.last4digits as string)
                    )}
                  </div>
                </div>
                <div className="flex flex-row items-center gap-3" onClick={(e) => e.stopPropagation()}>
                  {isCardSelected(card) && <img src={Checked} className="w-4 h-4" />}
                  <Popover
                    trigger={
                      <div className="flex items-center justify-center min-w-4 min-h-4 p-2">
                        <FontAwesomeIcon icon="fa-solid fa-ellipsis-vertical" className="text-xs! cursor-pointer" />
                      </div>
                    }
                    isOpen={openPopoverId === cardId}
                    onOpenChange={(open) => setOpenPopoverId(open ? cardId : null)}
                  >
                    <PopoverItem
                      data-testid={`delete-card-${index}`}
                      variant="danger"
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleDelete(card, event)}
                    >
                      <div className="flex flex-row items-center gap-2">
                        <FontAwesomeIcon icon="fa-regular fa-trash" className="text-xs! cursor-pointer" />
                        {t('delete')}
                      </div>
                    </PopoverItem>
                  </Popover>
                </div>
              </div>
            </Select>
          );
        })}
      </div>
      <div className="flex flex-col gap-2 mt-2 w-full text-sm">
        <Button data-testid="select-card-button" type={ButtonType.DARK} onClick={handleSelect}>
          {t('select')}
        </Button>
        <Button data-testid="cancel-card-selection-button" type={ButtonType.LIGHT} onClick={handleCancel}>
          {t('cancel')}
        </Button>
      </div>
    </div>
  );
};
